/**
 * Global Search – Zustand store
 *
 * Manages query state, search results, typeahead suggestions, AI chat messages,
 * recent-search history, and keyboard-navigation index.
 */

import { create } from 'zustand';
import type {
  SearchStoreState,
  SearchFiltersPayload,
  AIChatMessage,
  AIChatStreamEvent,
  SearchResultItem,
} from '../types';
import * as searchApi from '../services/searchApi';
import {
  captureWorkspaceGeneration,
  isWorkspaceGenerationCurrent,
} from '../../../auth/workspaceGeneration';
import { registerWorkspaceCacheReset } from '../../../auth/workspaceCaches';

// ─── Constants ───────────────────────────────────────────────────────────────

const RECENT_SEARCHES_KEY = 'global_search_recent';
const MAX_RECENT_SEARCHES = 10;

function loadRecentSearches(): { query: string; timestamp: number; resultCount: number }[] {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function persistRecentSearches(items: { query: string; timestamp: number; resultCount: number }[]): void {
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(items));
}

const DEFAULT_FILTERS: SearchFiltersPayload = {};

// ─── Store ───────────────────────────────────────────────────────────────────

/** Active SSE AbortController — module-scoped so the store can cancel it. */
let activeStreamController: AbortController | null = null;

export const useSearchStore = create<SearchStoreState>((set, get) => ({
  // ── Query ──────────────────────────────────────────────────────────────
  query: '',
  setQuery: (q) => set({ query: q }),

  // ── Filters ────────────────────────────────────────────────────────────
  filters: { ...DEFAULT_FILTERS },
  setFilters: (f) => set({ filters: f }),
  resetFilters: () => set({ filters: { ...DEFAULT_FILTERS } }),

  // ── Full-text results ──────────────────────────────────────────────────
  results: [],
  total: 0,
  page: 1,
  pageSize: 20,
  facets: [],
  queryTimeMs: 0,
  resultsLoading: false,
  resultsError: null,

  fetchResults: async (query, filters, page = 1) => {
    const workspaceGeneration = captureWorkspaceGeneration();
    set({ resultsLoading: true, resultsError: null });
    try {
      const response = await searchApi.search({
        query,
        filters: filters ?? get().filters,
        page,
        pageSize: get().pageSize,
      });
      if (!isWorkspaceGenerationCurrent(workspaceGeneration)) return;

      set({
        results: response.results,
        total: response.total,
        page: response.page,
        facets: response.facets,
        queryTimeMs: response.queryTimeMs,
        resultsLoading: false,
      });
    } catch (err: unknown) {
      if (!isWorkspaceGenerationCurrent(workspaceGeneration)) return;

      const message = err instanceof Error ? err.message : 'Search request failed';
      set({ resultsLoading: false, resultsError: message });
    }
  },

  // ── Typeahead ──────────────────────────────────────────────────────────
  suggestions: [],
  suggestionsLoading: false,

  fetchSuggestions: async (query) => {
    if (!query.trim()) {
      set({ suggestions: [] });
      return;
    }
    const workspaceGeneration = captureWorkspaceGeneration();
    set({ suggestionsLoading: true });
    try {
      const response = await searchApi.typeahead({ query, limit: 8 });
      if (!isWorkspaceGenerationCurrent(workspaceGeneration)) return;

      set({ suggestions: response.suggestions, suggestionsLoading: false });
    } catch {
      if (!isWorkspaceGenerationCurrent(workspaceGeneration)) return;

      set({ suggestionsLoading: false });
    }
  },

  clearSuggestions: () => set({ suggestions: [] }),

  // ── AI chat ────────────────────────────────────────────────────────────
  chatMessages: [],
  chatConversationId: null,
  chatSources: [],
  chatLoading: false,
  chatStreaming: false,
  chatError: null,

  sendChatMessage: async (message) => {
    const workspaceGeneration = captureWorkspaceGeneration();
    const userMsg: AIChatMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };
    set((s) => ({
      chatMessages: [...s.chatMessages, userMsg],
      chatLoading: true,
      chatError: null,
    }));
    try {
      const response = await searchApi.chat({
        message,
        conversationId: get().chatConversationId ?? undefined,
      });
      if (!isWorkspaceGenerationCurrent(workspaceGeneration)) return;

      set((s) => ({
        chatMessages: [...s.chatMessages, response.message],
        chatConversationId: response.conversationId,
        chatSources: response.sources,
        chatLoading: false,
      }));
    } catch (err: unknown) {
      if (!isWorkspaceGenerationCurrent(workspaceGeneration)) return;

      const errMsg = err instanceof Error ? err.message : 'AI request failed';
      set({ chatLoading: false, chatError: errMsg });
    }
  },

  streamChatMessage: (message) => {
    const workspaceGeneration = captureWorkspaceGeneration();
    const userMsg: AIChatMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };

    const assistantMsg: AIChatMessage = {
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
    };

    set((s) => ({
      chatMessages: [...s.chatMessages, userMsg, assistantMsg],
      chatStreaming: true,
      chatError: null,
      chatSources: [],
    }));

    activeStreamController = searchApi.chatStream(
      { message, conversationId: get().chatConversationId ?? undefined },
      (event: AIChatStreamEvent) => {
        if (!isWorkspaceGenerationCurrent(workspaceGeneration)) return;

        const { chatMessages } = get();
        const lastIdx = chatMessages.length - 1;

        switch (event.type) {
          case 'token': {
            const updated = [...chatMessages];
            updated[lastIdx] = {
              ...updated[lastIdx],
              content: updated[lastIdx].content + event.data,
            };
            set({ chatMessages: updated });
            break;
          }
          case 'source': {
            try {
              const source: SearchResultItem = JSON.parse(event.data);
              set((s) => ({ chatSources: [...s.chatSources, source] }));
            } catch {
              // ignore malformed source events
            }
            break;
          }
          case 'done':
            set({ chatStreaming: false });
            break;
          case 'error':
            set({ chatStreaming: false, chatError: event.data });
            break;
        }
      },
      (error) => {
        if (!isWorkspaceGenerationCurrent(workspaceGeneration)) return;

        set({ chatStreaming: false, chatError: error.message });
      },
      () => {
        if (!isWorkspaceGenerationCurrent(workspaceGeneration)) return;

        set({ chatStreaming: false });
      },
    );
  },

  abortChatStream: () => {
    activeStreamController?.abort();
    activeStreamController = null;
    set({ chatStreaming: false });
  },

  clearChat: () =>
    set({
      chatMessages: [],
      chatConversationId: null,
      chatSources: [],
      chatLoading: false,
      chatStreaming: false,
      chatError: null,
    }),

  // ── AI drawer ──────────────────────────────────────────────────────────
  aiDrawerOpen: false,
  setAiDrawerOpen: (open) => set({ aiDrawerOpen: open }),

  // ── Recent searches ────────────────────────────────────────────────────
  recentSearches: loadRecentSearches(),

  addRecentSearch: (query, resultCount) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    set((s) => {
      const filtered = s.recentSearches.filter((r) => r.query !== trimmed);
      const next = [{ query: trimmed, timestamp: Date.now(), resultCount }, ...filtered].slice(
        0,
        MAX_RECENT_SEARCHES,
      );
      persistRecentSearches(next);
      return { recentSearches: next };
    });
  },

  clearRecentSearches: () => {
    try {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
    } catch (error) {
      console.warn('Unable to clear recent search history from local storage', error);
    }
    set({ recentSearches: [] });
  },

  // ── Keyboard navigation ────────────────────────────────────────────────
  highlightedIndex: -1,
  setHighlightedIndex: (i) => set({ highlightedIndex: i }),
}));

registerWorkspaceCacheReset('global-search', () => {
  const searchStore = useSearchStore.getState();
  searchStore.abortChatStream();
  searchStore.clearRecentSearches();
  useSearchStore.setState({
    query: '',
    filters: {},
    results: [],
    total: 0,
    page: 1,
    facets: [],
    queryTimeMs: 0,
    resultsLoading: false,
    resultsError: null,
    suggestions: [],
    suggestionsLoading: false,
    chatMessages: [],
    chatConversationId: null,
    chatSources: [],
    chatLoading: false,
    chatStreaming: false,
    chatError: null,
    aiDrawerOpen: false,
    highlightedIndex: -1,
  });
});
